import type { TAuthStore } from '~/stores/useAuthStore';
import type { UserToken } from '~/types/UserToken';

import Keycloak, { type KeycloakConfig } from 'keycloak-js';
import { jwtDecode } from 'jwt-decode';

let store: TAuthStore;
let keycloak = new Keycloak();

/**
 * Initializes keycloak, then run callback.
 * @param onInitCallback
 */
async function init(
  kcConfig: KeycloakConfig,
  onInitCallback: () => void | Promise<void>,
): Promise<void> {
  keycloak = new Keycloak(kcConfig);

  try {
    await keycloak
      .init({
        onLoad: 'check-sso',
        pkceMethod: 'S256',
      })
      .then(async (authenticated: boolean) => {
        if (authenticated && store) {
          updateStore();
          onInitCallback();
          keycloak.onTokenExpired = () => {
            refreshToken();
          };
        } else {
          onInitCallback();
        }
      });
  } catch (error: any) {
    console.error('something wrong initializing keycloak', error);
    throw new Error(`something wrong initializing keycloak`, error);
  }
}

/**
 * Initializes store, with keycloak user data.
 * @param storeInstance
 */
function initStore(storeInstance: TAuthStore) {
  store = storeInstance;
  store.onInitOauth(keycloak);
}

function updateStore(clearData = false): void {
  if (store.authenticated) {
    return store.onUpdateOauth(keycloak);
  }

  return store.onInitOauth(keycloak, clearData);
}

/**
 * Login user, then redirect to defined uri.
 */
async function login() {
  try {
    await keycloak.login();
  } catch (error: any) {
    throw new Error(`something wrong logging in`, error);
  }
}

/**
 * Logout user, then redirect to defined uri.
 */
function logout() {
  keycloak.logout();
}

async function isTokenExpired() {
  return await keycloak.isTokenExpired();
}

/**
 * Refresh token.
 * @returns { KeycloakInstance } keycloak
 */
async function refreshToken() {
  await keycloak.updateToken().then((refreshed) => {
    if (refreshed) {
      updateStore();
      return refreshed;
    }
  });
}

/**
 * Defines the tenant based on the provided token.
 * @param token - The token used to decode and determine the tenant.
 * @returns The tenant ID or undefined if the token is invalid.
 */
function defineTenant(token: string): string | undefined {
  const decodedToken: unknown = jwtDecode(token);
  if (!isUserToken(decodedToken)) return undefined;

  const { tenantId } = decodedToken;

  if (tenantId.length > 1) {
    const matriz = tenantId.find((tenantTaxId: string | undefined) =>
      tenantTaxId?.includes('0001/'),
    );

    return matriz ?? tenantId[0];
  }
  return tenantId[0];

  function isUserToken(obj: unknown): obj is UserToken {
    return obj instanceof Object && 'tenantId' in obj && Array.isArray(obj.tenantId);
  }
}

export default {
  CallInit: init,
  CallInitStore: initStore,
  CallUpdateStore: updateStore,
  CallLogin: login,
  CallLogout: logout,
  CallTokenRefresh: refreshToken,
  CallDefineTenant: defineTenant,
  CallIsTokenExpired: isTokenExpired,
};
